import { useState, useEffect, Suspense, lazy } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Users, ExternalLink, Loader2, Sparkles, Trash2, Handshake, Upload, X } from "lucide-react";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { PartnerFormDialog } from "../components/partners/PartnerFormDialog";
import { PartnerDrawer } from "../components/partners/PartnerDrawer";
import { CsvMigrationDialog } from "../components/partners/CsvMigrationDialog";
import { MultiSelectCombobox } from "../components/shared/MultiSelectCombobox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const DealflowPipelinePage = lazy(() => import("./DealflowPipelinePage"));

const FILTER_DEFS = [
  { key: "country", label: "País", catalog: null },
  { key: "agentType", label: "Tipo de Agente", catalog: "type" },
  { key: "specialty", label: "Especialidad", catalog: "specialty" },
  { key: "origin", label: "Origen", catalog: "origin" },
  { key: "fitNegocio", label: "Fit negocio", catalog: "fit_negocio" },
  { key: "pipeline", label: "Pipeline", catalog: "pipeline" },
  { key: "statusFilter", label: "Status", catalog: "status" },
  { key: "batch", label: "Colección", catalog: "batch" },
  { key: "eventLeads", label: "Event Leads", catalog: "event_leads" },
];
const EMPTY_FILTERS = { country: [], agentType: [], specialty: [], origin: [], fitNegocio: [], pipeline: [], statusFilter: [], batch: [], eventLeads: [] };

export default function PartnersPage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "dealflow" ? "dealflow" : "directorio");
  const [partners, setPartners] = useState([]);
  const [unifiedMeta, setUnifiedMeta] = useState({ total: 0, total_curated: 0, total_ecosystem: 0, by_type: {}, country_options: [] });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [sourceFilter, setSourceFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [catalogs, setCatalogs] = useState(null);
  const [drawerAgent, setDrawerAgent] = useState(null);
  const { token, user } = useAuth();

  const fetchData = async () => {
    try {
      const data = await api.getPartnersUnified(token, { search: searchTerm, source: sourceFilter, page, pageSize, ...filters });
      setPartners(data.items);
      setUnifiedMeta(data);
    } catch (error) {
      toast.error("Error al cargar el ecosistema");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.getPartnerCatalogs(token).then(setCatalogs).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, sourceFilter, filters]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDeletePartner = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deletePartner(token, deleteTarget.curated_id);
      toast.success("Agente eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleAISearch = async () => {
    if (!searchQuery.trim()) return;
    setAiSearching(true);
    try {
      const results = await api.aiSearch(token, searchQuery, "startups");
      setAiResults(results);
    } catch (error) {
      toast.error("Error en la búsqueda IA");
    } finally {
      setAiSearching(false);
    }
  };

  const activeFilterCount = Object.values(filters).filter((v) => v.length > 0).length + (sourceFilter ? 1 : 0);
  const clearAllFilters = () => { setFilters(EMPTY_FILTERS); setSourceFilter(""); setPage(1); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="partners-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ecosistema y Partners</h1>
          <p className="text-muted-foreground mt-1">
            Startups, centros tecnológicos y partners estratégicos
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={searchDialogOpen} onOpenChange={setSearchDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="ai-search-partners-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Buscar Startups IA
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Innovation Radar - Búsqueda de Startups</DialogTitle>
                <DialogDescription>Encuentra startups y partners potenciales con IA</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Ej: startups de IoT para gestión de residuos, cleantech..."
                    onKeyDown={(e) => e.key === "Enter" && handleAISearch()}
                  />
                  <Button onClick={handleAISearch} disabled={aiSearching}>
                    {aiSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
                  </Button>
                </div>
                {aiResults && (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Startups encontradas</h4>
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <pre className="whitespace-pre-wrap text-sm">{aiResults.results}</pre>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          {user?.role === "admin" && (
            <Button variant="outline" onClick={() => setCsvDialogOpen(true)} data-testid="csv-migration-open-btn">
              <Upload className="w-4 h-4 mr-2" />
              Migrar desde CSV
            </Button>
          )}
          <Button onClick={() => setFormDialogOpen(true)} data-testid="create-partner-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Agente
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="directorio" data-testid="partners-tab-directorio"><Users className="w-4 h-4 mr-2" />Directorio</TabsTrigger>
          <TabsTrigger value="dealflow" data-testid="partners-tab-dealflow"><Handshake className="w-4 h-4 mr-2" />Pipeline</TabsTrigger>
        </TabsList>

        <TabsContent value="directorio" className="space-y-6 mt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="kpi-card" data-testid="partners-stat-total">
              <CardContent className="p-4">
                <p className="overline">Total (agentes distintos)</p>
                <p className="text-3xl font-bold mt-1">{unifiedMeta.total}</p>
              </CardContent>
            </Card>
            <Card className="kpi-card cursor-pointer" onClick={() => { setSourceFilter(sourceFilter === "curated" ? "" : "curated"); setPage(1); }} data-testid="partners-stat-curated">
              <CardContent className="p-4">
                <p className="overline">Curados</p>
                <p className="text-3xl font-bold mt-1">{unifiedMeta.total_curated}</p>
              </CardContent>
            </Card>
            <Card className="kpi-card cursor-pointer" onClick={() => { setSourceFilter(sourceFilter === "airtable_ecosystem" ? "" : "airtable_ecosystem"); setPage(1); }} data-testid="partners-stat-ecosystem">
              <CardContent className="p-4">
                <p className="overline">Ecosistema (Airtable)</p>
                <p className="text-3xl font-bold mt-1">{unifiedMeta.total_ecosystem}</p>
              </CardContent>
            </Card>
            <Card className="kpi-card">
              <CardContent className="p-4">
                <p className="overline">Categorías</p>
                <p className="text-3xl font-bold mt-1">{Object.keys(unifiedMeta.by_type || {}).length}</p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-3">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por cualquier campo: nombre, tagline, país, web, especialidad, origen..."
              className="max-w-md"
              data-testid="partners-search-input"
            />
            <div className="flex flex-wrap gap-2 items-start" data-testid="partners-filter-bar">
              {FILTER_DEFS.map((f) => {
                const opts = f.key === "country" ? (unifiedMeta.country_options || []) : (catalogs?.[f.catalog] || []);
                return (
                  <div key={f.key} className="w-[170px]">
                    <MultiSelectCombobox
                      options={opts}
                      value={filters[f.key]}
                      onChange={(v) => { setFilters((old) => ({ ...old, [f.key]: v })); setPage(1); }}
                      placeholder={f.label}
                      testId={`partners-filter-${f.key}`}
                    />
                  </div>
                );
              })}
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="gap-1 h-9" data-testid="partners-clear-filter-btn">
                  <X className="w-3.5 h-3.5" />Quitar filtros ({activeFilterCount})
                </Button>
              )}
            </div>
          </div>

          {partners.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium mb-2">No hay agentes</h3>
                <p className="text-muted-foreground mb-4">Añade un agente o busca startups con IA</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={() => setSearchDialogOpen(true)}><Sparkles className="w-4 h-4 mr-2" />Buscar con IA</Button>
                  <Button onClick={() => setFormDialogOpen(true)}><Plus className="w-4 h-4 mr-2" />Añadir Manual</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="rounded-lg border overflow-x-auto" data-testid="partners-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Tagline</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>País</TableHead>
                      <TableHead>Web</TableHead>
                      <TableHead>Tipo de Agente</TableHead>
                      <TableHead>Especialidad</TableHead>
                      <TableHead>Fit negocio</TableHead>
                      <TableHead>Origen</TableHead>
                      <TableHead>Colección</TableHead>
                      <TableHead>Pipeline</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partners.map((p) => (
                      <TableRow key={p.row_id} className="cursor-pointer" onClick={() => setDrawerAgent(p)} data-testid={`partner-row-${p.row_id}`}>
                        <TableCell className="font-medium max-w-[180px]">
                          <button className="text-left hover:underline flex items-center gap-2" data-testid={`partner-open-${p.row_id}`}>
                            {p.name}
                            {p.is_demo && <Badge variant="outline" className="border-amber-400 text-amber-700 text-[10px]">Demo</Badge>}
                          </button>
                          <div className="flex gap-1 mt-1">
                            {p.sources.includes("curated") && <Badge className="text-[10px]" variant="default">Curado</Badge>}
                            {p.sources.includes("airtable_ecosystem") && <Badge className="text-[10px]" variant="secondary">Ecosistema</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[160px] truncate text-muted-foreground text-sm" title={p.tagline}>{p.tagline}</TableCell>
                        <TableCell className="max-w-[220px] truncate text-muted-foreground text-sm">{p.description}</TableCell>
                        <TableCell className="text-sm">{p.country}</TableCell>
                        <TableCell>
                          {p.website && (
                            <a href={p.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary hover:underline inline-flex items-center gap-1 text-sm">
                              <ExternalLink className="w-3.5 h-3.5" />Web
                            </a>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{p.type && <Badge variant="outline">{p.type}</Badge>}</TableCell>
                        <TableCell className="max-w-[160px]">
                          <div className="flex flex-wrap gap-1">
                            {(p.specialty || []).slice(0, 3).map((s) => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}
                            {(p.specialty || []).length > 3 && <span className="text-xs text-muted-foreground">+{p.specialty.length - 3}</span>}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[140px]">
                          <div className="flex flex-wrap gap-1">
                            {(p.fit_negocio || []).slice(0, 2).map((s) => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}
                            {(p.fit_negocio || []).length > 2 && <span className="text-xs text-muted-foreground">+{p.fit_negocio.length - 2}</span>}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[140px]">
                          <div className="flex flex-wrap gap-1">
                            {(p.origin || []).slice(0, 2).map((s) => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}
                            {(p.origin || []).length > 2 && <span className="text-xs text-muted-foreground">+{p.origin.length - 2}</span>}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[140px] text-sm text-muted-foreground truncate">{(p.batch || []).join(", ")}</TableCell>
                        <TableCell className="text-sm text-muted-foreground truncate max-w-[120px]">{p.pipeline}</TableCell>
                        <TableCell className="max-w-[140px]">
                          <div className="flex flex-wrap gap-1">
                            {(p.status || []).slice(0, 2).map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {p.curated_id && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }} data-testid={`partner-delete-${p.row_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-center gap-3 pt-2" data-testid="partners-pagination">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="partners-prev-page-btn">Anterior</Button>
                <span className="text-sm text-muted-foreground">
                  Página {page} de {Math.max(1, Math.ceil((unifiedMeta.total || 0) / pageSize))} · {unifiedMeta.total} resultados
                </span>
                <Button variant="outline" size="sm" disabled={page >= Math.ceil((unifiedMeta.total || 0) / pageSize)} onClick={() => setPage((p) => p + 1)} data-testid="partners-next-page-btn">Siguiente</Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="dealflow" className="space-y-4 mt-0" data-testid="partners-dealflow-panel">
          {/* Fix delta P1 punto 8 (2026-08-31): se quita la sub-pestaña "Deals
              (Airtable)" — sus campos (importe, prob. cierre, negocio, inicio)
              ahora se fusionan directamente en cada ficha de Pipeline B2B. */}
          <Suspense fallback={<div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>}>
            <DealflowPipelinePage />
          </Suspense>
        </TabsContent>
      </Tabs>

      <PartnerFormDialog open={formDialogOpen} onOpenChange={setFormDialogOpen} catalogs={catalogs} onSaved={fetchData} />
      <PartnerDrawer open={!!drawerAgent} agent={drawerAgent} catalogs={catalogs} onClose={() => setDrawerAgent(null)} onSaved={() => { fetchData(); setDrawerAgent(null); }} />
      <CsvMigrationDialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen} onImported={fetchData} />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar agente"
        description={`¿Estás seguro de que quieres eliminar "${deleteTarget?.name}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={confirmDeletePartner}
        loading={deleting}
        testId="delete-partner-confirm-dialog"
      />
    </div>
  );
}
